import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

export interface VendorProductMerged {
  // Shopify data
  shopifyProductId: string;
  shopifyVariantId: string;
  shopifyInventoryItemId: string;
  productTitle: string;
  variantTitle: string;
  productType: string;
  productVendor: string;
  tags: string[];
  selectedOptions: Array<{ name: string; value: string }>;
  sku: string;
  price: string;
  unitCost: string | null;
  qoh: number;
  // DB record (null if unmapped)
  dbRecord: {
    id: string;
    vendorSku: string | null;
    category: string | null;
    packSize: number;
    caseCost: number;
    offInvoice: number;
    byCheque: number;
    freeGoods: number;
    productMinLevel: number | null;
    isDiscontinued: boolean;
    manualOrderQty: number | null;
    flaggedForReview: boolean;
    reviewNote: string | null;
    sortOrder: number;
  } | null;
  // Calculated costs
  calculated: {
    netCaseCost: number;
    unitCost: number;
    margin: number;
  } | null;
}

export function useVendorProducts() {
  const authFetch = useAuthenticatedFetch();
  const [products, setProducts] = useState<VendorProductMerged[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = useCallback(
    async (vendorId: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(`/api/vendors/${vendorId}/products`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Failed to load products");
        setProducts(json.data || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  const saveProducts = useCallback(
    async (
      vendorId: string,
      items: Array<{
        shopifyVariantId: string;
        shopifyProductId: string;
        caseCost: number;
        packSize?: number;
        vendorSku?: string;
        category?: string;
        offInvoice?: number;
        byCheque?: number;
        freeGoods?: number;
        productMinLevel?: number | null;
        isDiscontinued?: boolean;
        manualOrderQty?: number | null;
        flaggedForReview?: boolean;
        reviewNote?: string | null;
        sortOrder?: number;
      }>
    ): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/vendors/${vendorId}/products/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(items),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const refreshFromShopify = useCallback(
    async (vendorId: string): Promise<boolean> => {
      try {
        const res = await authFetch(
          `/api/vendors/${vendorId}/products/sync`,
          { method: "POST" }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  return {
    products,
    loading,
    error,
    loadProducts,
    saveProducts,
    refreshFromShopify,
  };
}
